import { ImprovedIcon, NotImprovedIcon, CrownIcon, ZeroIcon, CrossedLimitIcon } from 'assests/GmsIcons';
import React from 'react';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { RESULT_LEGEND } from './ResultLegend';

type TResultStatusProps = {
    status?: string;
    modelName: string;
}

/**
 * A model name with its outcome glyph, in the list page's Models column (§ 162).
 *
 * The glyph is the ONLY carrier of the outcome — won, crossed limit, improved, not
 * improved, zero — and it had no accessible name, so a screen reader heard four model
 * names and nothing about how any of them did. It is labelled now.
 *
 * The labels come from `RESULT_LEGEND` — the same list the Result column's tooltip
 * renders — so the words a user reads in the legend are exactly the words announced
 * here. Two hand-maintained copies of five strings is how they drift.
 */
const STATUS_LABELS: Record<string, string> = Object.fromEntries(
    RESULT_LEGEND.map((entry) => [entry.id, entry.label]),
);

const getIconBasedOnStatus = (status?: string,) => {
    if (!status) return null;

    switch (status) {
        case 'won':
            return <CrownIcon />;
        case 'crossed_limit':
            return <CrossedLimitIcon />;
        case 'improved':
            return <ImprovedIcon />;
        case 'not_improved':
            return <NotImprovedIcon />;
        case 'zero':
            return <ZeroIcon />;
        default:
            return null;
    }
};

const ResultStatus = ({ status, modelName }: TResultStatusProps) => {
    const icon = getIconBasedOnStatus(status);
    const statusLabel = status ? STATUS_LABELS[status] : undefined;

    return (
        <div className="tw-flex tw-items-center tw-gap-1 tw-rounded-ctl">
            {status && (
                <span aria-hidden="true">{icon}</span>
            )}
            {/* The name and the outcome read as one phrase — "New Model A, Improved" —
                rather than the outcome arriving as a separate unnamed graphic. */}
            {statusLabel ? <span className="tw-sr-only">{`, ${statusLabel}`}</span> : null}
            <Typography variant="body-primary" as="span" className="tw-text-ink tw-py-1">
                {modelName}
            </Typography>
        </div>
    );
}

export default ResultStatus;
